// Pure, Phaser-free grid movement + interact-facing logic so it can be unit
// tested directly (see movement.test.ts) and reused by LibraryScene.

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface TileCoord {
  x: number;
  y: number;
}

export interface CollisionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MovementContext {
  mapWidthInTiles: number;
  mapHeightInTiles: number;
  tileSize: number;
  /** Returns true if a solid (wall/furniture) tile occupies this tile coordinate. */
  isTileSolid: (tile: TileCoord) => boolean;
  collisionRects: CollisionRect[];
}

const DIRECTION_DELTAS: Record<Direction, TileCoord> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

export function getNextTile(tile: TileCoord, direction: Direction): TileCoord {
  const delta = DIRECTION_DELTAS[direction];
  return { x: tile.x + delta.x, y: tile.y + delta.y };
}

export function isWithinMapBounds(tile: TileCoord, ctx: MovementContext): boolean {
  return tile.x >= 0 && tile.x < ctx.mapWidthInTiles && tile.y >= 0 && tile.y < ctx.mapHeightInTiles;
}

export function isBlockedByCollisionRect(tile: TileCoord, ctx: MovementContext): boolean {
  const centerX = tile.x * ctx.tileSize + ctx.tileSize / 2;
  const centerY = tile.y * ctx.tileSize + ctx.tileSize / 2;
  return ctx.collisionRects.some(
    (rect) => centerX >= rect.x && centerX < rect.x + rect.width && centerY >= rect.y && centerY < rect.y + rect.height
  );
}

/** Whether the player may step onto `tile` from wherever they currently are. */
export function canMoveTo(tile: TileCoord, ctx: MovementContext): boolean {
  if (!isWithinMapBounds(tile, ctx)) return false;
  if (ctx.isTileSolid(tile)) return false;
  if (isBlockedByCollisionRect(tile, ctx)) return false;
  return true;
}

export interface Interactable {
  tile: TileCoord;
  name: string;
  message?: string;
}

/** Finds the interactable (if any) sitting on the tile the player is facing. */
export function findInteractableAt(tile: TileCoord, interactables: Interactable[]): Interactable | undefined {
  return interactables.find((obj) => obj.tile.x === tile.x && obj.tile.y === tile.y);
}
